import { localize, readAppLanguage } from '../i18n';
import useNavigate from './useSafeNavigate';
import DialogTitle from '../components/ui/FitDialogTitle';
import Button from '../components/ui/FitButton';
import { journey } from '../utils/productJourneys';
import Dialog from '../components/StableDialog';
import LoadingLabel from '../components/LoadingLabel';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DialogActions, DialogContent, Typography } from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import {
    claimMedicationSlotRewardBonus,
    getDeviceUuid,
    getMedicationLimit,
} from '../api';
import { requestAd, rewardedAdFailureMessage } from '../utils/adRequests';
import AppToast from '../components/AppToast';
import { useRegistrationDraft } from '../components/RegistrationDraftProvider';
import RegistrationDraftChoice from '../components/RegistrationDraftChoice';
import { discardRegistrationDraft, readRegistrationDraft, registrationOwner } from '../utils/registrationDraft';

const DEFAULT_PATH = '/medications/new-bundle?mode=manual';
const REWARD_FEATURE = 'medication_slot_reward';

export default function useMedicationAddGate() {
    const draftState = useRegistrationDraft();
    const navigate = useNavigate();
    const checkingRef = useRef(false);
    const [checkingMedicationLimit, setCheckingMedicationLimit] = useState(false);
    const [dialog, setDialog] = useState({ open: false, pendingPath: DEFAULT_PATH, pendingOptions: null, limit: null });
    const [rewardSubmitting, setRewardSubmitting] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
    const [choice, setChoice] = useState(null);
    const replacementRef = useRef(null);
    const enterNew = useCallback((path, options) => {
        if (replacementRef.current && !discardRegistrationDraft(replacementRef.current)) {
            setToast({ open: true, severity: 'warning', message: localize("다른 화면에서 작성 내용이 바뀌었어요. 다시 열어주세요.") });
            replacementRef.current = null; setChoice(null); return;
        }
        replacementRef.current = null; setChoice(null);
        navigate(path, options || undefined);
    }, [navigate]);

    const openMedicationAdd = useCallback(async (path = DEFAULT_PATH, options = null, intent = null) => {
        if (checkingRef.current) return;
        if (draftState) {
            if (!registrationOwner()) await draftState.refresh();
            if (!registrationOwner()) {
                setToast({ open: true, severity: 'error', message: localize("작성 내용을 확인하지 못했어요. 연결을 확인하고 다시 시도해주세요.") }); return;
            }
            const existing = readRegistrationDraft();
            if (existing) {
                if (existing.status === 'pending' || intent === 'resume') {
                    navigate(existing.path, { state: { ...options?.state, registrationDraftId: existing.id } }); return;
                }
                setChoice({ draft: existing, path, options, replacing: intent === 'new' }); return;
            }
        }
        checkingRef.current = true;
        setCheckingMedicationLimit(true);
        try {
            const limit = await getMedicationLimit(getDeviceUuid());
            if (limit.limitExempt || (limit.remaining ?? 0) > 0) {
                enterNew(path, options);
                return;
            }
            journey('slot_reward', 'limit', 'menu');
            setDialog({ open: true, pendingPath: path, pendingOptions: options, limit });
        } catch {
            setToast({
                open: true,
                message: localize("등록 가능 개수를 확인하지 못했어요. 잠시 후 다시 시도해주세요."),
                severity: 'error',
            });
        } finally {
            checkingRef.current = false;
            setCheckingMedicationLimit(false);
        }
    }, [navigate, enterNew, draftState]);

    const replaceDraft = useCallback(async (existing = null, path = null, options = null) => {
        const target = existing?.id ? { draft: existing, path, options } : choice;
        if (!target || checkingRef.current) return;
        if (choice) setChoice({ ...target, replacing: true });
        checkingRef.current = true; setCheckingMedicationLimit(true);
        replacementRef.current = target.draft;
        try {
            const limit = await getMedicationLimit(getDeviceUuid());
            if (limit.limitExempt || (limit.remaining ?? 0) > 0) enterNew(target.path, target.options);
            else {
                setDialog({ open: true, pendingPath: target.path, pendingOptions: target.options, limit });
                setChoice(null);
            }
        } catch {
            replacementRef.current = null;
            setToast({ open: true, severity: 'error', message: localize("등록 가능 개수를 확인하지 못했어요. 작성 내용은 보관했어요.") });
        } finally { checkingRef.current = false; setCheckingMedicationLimit(false); }
    }, [choice, enterNew]);

    const closeDialog = useCallback(() => {
        if (rewardSubmitting) return;
        setDialog(prev => ({ ...prev, open: false }));
        replacementRef.current = null;
    }, [rewardSubmitting]);

    const claimReward = useCallback(async (reason = null) => {
        if (!dialog.open) return;
        setRewardSubmitting(true);
        try {
            const limit = await claimMedicationSlotRewardBonus(getDeviceUuid());
            if (!limit.success) {
                setToast({
                    open: true,
                    message: localize(limit.message) || localize("보너스를 받을 수 없어요"),
                    severity: 'warning',
                });
                return;
            }
            const nextPath = dialog.pendingPath || DEFAULT_PATH;
            const nextOptions = dialog.pendingOptions || undefined;
            setDialog({ open: false, pendingPath: DEFAULT_PATH, pendingOptions: null, limit });
            setToast({
                open: true,
                message: reason === 'ad_free'
                    ? localize("광고 제거 혜택으로 약 {v0}개를 더 등록할 수 있어요", { v0: limit.rewardSlots || 3 })
                    : localize("약 {v0}개를 더 등록할 수 있어요", { v0: limit.rewardSlots || 3 }),
                severity: 'success',
            });
            enterNew(nextPath, nextOptions);
        } catch {
            setToast({
                open: true,
                message: localize("보너스 처리 중 오류가 발생했어요"),
                severity: 'error',
            });
        } finally {
            setRewardSubmitting(false);
        }
    }, [dialog.open, dialog.pendingOptions, dialog.pendingPath, enterNew]);

    useEffect(() => {
        const handler = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.feature !== REWARD_FEATURE) return;
                if (data.type === 'REWARDED_AD_COMPLETE') claimReward(data.reason || null);
                if (data.type === 'REWARDED_AD_UNAVAILABLE') {
                    setToast({
                        open: true,
                        message: rewardedAdFailureMessage(data.reason),
                        severity: 'warning',
                    });
                }
            } catch {}
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [claimReward]);

    const requestReward = useCallback(() => {
        const requested = requestAd('rewarded', REWARD_FEATURE);
        if (!requested) {
            setToast({
                open: true,
                message: localize("앱에서만 광고를 보고 등록 슬롯을 추가할 수 있어요."),
                severity: 'warning',
            });
        }
    }, []);

    const MedicationAddGateDialog = useCallback(() => (
        <>
            {choice && <RegistrationDraftChoice key={choice.draft.id} open draft={choice.draft} replacing={choice.replacing}
                busy={checkingMedicationLimit} onClose={() => setChoice(null)} onReplace={replaceDraft}
                onResume={() => { navigate(choice.draft.path, { state: { ...choice.options?.state, registrationDraftId: choice.draft.id } }); setChoice(null); }}
                onDiscard={() => { if (!discardRegistrationDraft(choice.draft)) setToast({ open: true, severity: 'warning', message: localize("작성 내용이 바뀌었어요. 다시 열어주세요.") }); setChoice(null); }} />}
            <Dialog
                open={dialog.open}
                onClose={closeDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#1a1a1a' }}>{localize("등록 가능한 약을 모두 썼어요")}</DialogTitle>
                <DialogContent sx={{ color: '#555', fontWeight: 600, lineHeight: 1.6 }}>{localize("광고를 보거나 광고 제거 혜택으로 현재 한도에서 약 ")}{dialog.limit?.rewardSlots || 3}{localize("개를 더 등록할 수 있어요.")}</DialogContent>
                <DialogActions sx={{ p: 2, pt: 0, ...(readAppLanguage() !== 'ko' && { flexDirection: 'column', alignItems: 'stretch', gap: 1, '& > :not(style) ~ :not(style)': { ml: 0 } }) }}>
                    <Button onClick={closeDialog} disabled={rewardSubmitting} sx={{ color: '#666', fontWeight: 700 }}>{localize("나중에")}</Button>
                    <Button
                        onClick={requestReward}
                        disabled={rewardSubmitting}
                        variant="contained"
                        startIcon={<PlayCircleIcon />}
                        sx={{
                            bgcolor: '#ffe566',
                            color: '#5a4a00',
                            fontWeight: 800,
                            borderRadius: 2,
                            '&:hover': { bgcolor: '#e8d040' },
                        }}
                    >
                        {rewardSubmitting ? <LoadingLabel /> : readAppLanguage() === 'ko' ? localize("등록 개수 늘리기") : <Typography component="span" sx={{ font: 'inherit', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{localize("등록 개수 늘리기")}</Typography>}
                    </Button>
                </DialogActions>
            </Dialog>
            <AppToast
                open={toast.open}
                message={localize(toast.message)}
                severity={toast.severity}
                onClose={() => setToast(prev => ({ ...prev, open: false }))}
            />
        </>
    ), [choice, checkingMedicationLimit, replaceDraft, navigate, closeDialog, dialog.limit?.rewardSlots, dialog.open, requestReward, rewardSubmitting, toast.message, toast.open, toast.severity]);

    return { openMedicationAdd, MedicationAddGateDialog, checkingMedicationLimit, replaceExistingDraft: replaceDraft };
}
